// Conversation flow authoring (L1b) — the operator loads a worker's raw Skill
// flows, edits a step, and saves. Talks to the L1a endpoints; the raw flow (with
// full node config) is the source of truth so the round-trip is faithful (the
// console projection elsewhere is display-only / lossy).

import type { IdentityProfile } from '@vysto/shared/bot-config/identity'
import { isAxiosError } from 'axios'

import {
  type KbApi,
  type KbChunk,
  type KbPreviewResult,
  type KbSource,
  type UnansweredQuestion,
} from './bot-config-data.js'
import { getTenantMembers } from './tenants-data.js'
import { apiClient } from '../../api/client.js'
import { type AssignableMember, getAssignableMembers } from '../oversight/oversight-data.js'
import {
  getAdminWorkerEditable,
  getWorkerEditable,
  saveAdminWorkerEditable,
  saveWorkerEditable,
} from '../workers/workers-data.js'

/** Owner-facing KB api (the session-scoped `/workers/:botId/kb` routes) — the
 *  client manages their own bot's Conocimiento from the editor. */
const ownerKbBase = (botId: string): string => `/workers/${encodeURIComponent(botId)}/kb`
/**
 * KbApi bound to one bot over the session-scoped owner routes, including the
 * diagnosis trio (chunks/preview/unanswered) the KbPanel renders off presence.
 */
export function ownerKbApi(botId: string): KbApi {
  return {
    list: async () => (await apiClient.get<{ data: KbSource[] }>(ownerKbBase(botId))).data.data,
    uploadUrl: async (url) => {
      await apiClient.post(ownerKbBase(botId), { kind: 'url', source_url: url })
    },
    uploadFile: async (file) => {
      const form = new FormData()
      form.append('file', file)
      await apiClient.post(ownerKbBase(botId), form)
    },
    approve: async (assetId) => {
      await apiClient.post(`${ownerKbBase(botId)}/${encodeURIComponent(assetId)}/approve`, {})
    },
    remove: async (assetId) => {
      await apiClient.delete(`${ownerKbBase(botId)}/${encodeURIComponent(assetId)}`)
    },
    chunks: async (assetId) =>
      (
        await apiClient.get<{ data: KbChunk[] }>(
          `${ownerKbBase(botId)}/${encodeURIComponent(assetId)}/chunks`,
        )
      ).data.data,
    preview: async (query) =>
      (await apiClient.post<{ data: KbPreviewResult }>(`${ownerKbBase(botId)}/preview`, { query }))
        .data.data,
    unanswered: async () =>
      (await apiClient.get<{ data: UnansweredQuestion[] }>(`${ownerKbBase(botId)}/unanswered`)).data
        .data,
  }
}

// ─── Owner conversation simulator (the `/workers/:botId/simulator` routes) ─────

/** One message in the simulator thread (as the chat UI renders it). */
export interface SimulatorMessage {
  id: string
  direction: 'inbound' | 'outbound'
  sender_type: string
  content: string | null
  status: string | null
  created_at: string
}

/** A sim conversation's state + messages. `state` (pending/active_bot/
 *  waiting_handoff/…) lets the chat UI show the handoff dead-end. */
export interface SimulatorThread {
  state: string
  messages: SimulatorMessage[]
  /** Who the handoff landed on, or null for the queue. Lets the panel say which
   *  way a routed transfer actually went instead of "con una persona" for both.
   *  Optional so an older/stubbed payload still renders. */
  assignedTo?: { id: string; name: string } | null
  /** How the last turn ended (P7). Lets the panel explain a turn that produced
   *  no reply instead of leaving the author staring at nothing. Optional so an
   *  older/stubbed payload still renders. */
  lastTurn?: { outcome: string; reason: string | null; at: string } | null
  /** Tool failures the last turn hit — the author's surface for a flow whose
   *  authored tool args stopped parsing (F42). Optional for the same reason. */
  toolFailures?: SimulatorToolFailure[]
  /** Where the rehearsal's card landed in the CRM (F6-1), or null while there
   *  is none. Optional so an older/stubbed payload still renders. */
  lead?: SimulatorLead | null
}

/** The card this rehearsal left in the funnel. Mirrors the engine's
 *  `SimulatorLead` (packages/engine/src/simulator/simulator.ts). */
export interface SimulatorLead {
  pipelineId: string
  pipelineName: string
  stageName: string
}

/** An author-facing tool failure on the last turn. Mirrors the engine's
 *  `SimulatorToolFailure` (packages/engine/src/simulator/simulator.ts). */
export interface SimulatorToolFailure {
  toolName: string
  error: string
  nodeId: string | null
  detail: string | null
}

/** Chat-with-your-own-bot, bound to one bot. The bot runs the full real
 *  pipeline; its replies are sinked (never sent to WhatsApp). */
export interface SimulatorApi {
  /** Ensure the sim conversation WITHOUT wiping (continue the thread); id. */
  open: () => Promise<string>
  /** Wipe + ensure a clean sim conversation; returns its id. */
  reset: () => Promise<string>
  /** Send a message as the customer; returns the (still pending) thread id. */
  send: (text: string) => Promise<string>
  /** Poll the thread (state + messages). */
  messages: (conversationId: string) => Promise<SimulatorThread>
  /** Give a handed-off conversation back to the bot so testing continues. */
  returnToBot: (conversationId: string) => Promise<void>
}

const ownerSimBase = (botId: string): string => `/workers/${encodeURIComponent(botId)}/simulator`

/**
 * SimulatorApi over the owner `/workers/:botId/simulator` routes — runs the
 * real bot pipeline with outbound sinked (nothing reaches WhatsApp).
 */
export function ownerSimulatorApi(botId: string): SimulatorApi {
  return {
    open: async () =>
      (
        await apiClient.post<{ data: { conversationId: string } }>(
          `${ownerSimBase(botId)}/open`,
          {},
        )
      ).data.data.conversationId,
    reset: async () =>
      (
        await apiClient.post<{ data: { conversationId: string } }>(
          `${ownerSimBase(botId)}/reset`,
          {},
        )
      ).data.data.conversationId,
    send: async (text) =>
      (
        await apiClient.post<{ data: { conversationId: string } }>(
          `${ownerSimBase(botId)}/message`,
          { text },
        )
      ).data.data.conversationId,
    messages: async (conversationId) =>
      (
        await apiClient.get<{ data: SimulatorThread }>(
          `${ownerSimBase(botId)}/messages?conversationId=${encodeURIComponent(conversationId)}`,
        )
      ).data.data,
    returnToBot: async (conversationId) => {
      await apiClient.post(`${ownerSimBase(botId)}/return-to-bot`, { conversationId })
    },
  }
}

/** Operator twin: same SimulatorApi against the /admin routes — tenant in the
 *  URL, because staff hold no membership in any client tenant. */
export function operatorSimulatorApi(tenantId: string, botId: string): SimulatorApi {
  const base = `/admin/tenants/${encodeURIComponent(tenantId)}/bots/${encodeURIComponent(botId)}/simulator`
  return {
    open: async () =>
      (await apiClient.post<{ data: { conversationId: string } }>(`${base}/open`, {})).data.data
        .conversationId,
    reset: async () =>
      (await apiClient.post<{ data: { conversationId: string } }>(`${base}/reset`, {})).data.data
        .conversationId,
    send: async (text) =>
      (await apiClient.post<{ data: { conversationId: string } }>(`${base}/message`, { text })).data
        .data.conversationId,
    messages: async (conversationId) =>
      (
        await apiClient.get<{ data: SimulatorThread }>(
          `${base}/messages?conversationId=${encodeURIComponent(conversationId)}`,
        )
      ).data.data,
    returnToBot: async (conversationId) => {
      await apiClient.post(`${base}/return-to-bot`, { conversationId })
    },
  }
}

/** Shared by the Ajustes form (page + modal). MX-first, the markets we run in. */
export const TIMEZONE_OPTIONS = [
  { value: 'America/Mexico_City', label: 'Centro — CDMX, Guadalajara, Monterrey (GMT-6)' },
  { value: 'America/Cancun', label: 'Sureste — Cancún, Quintana Roo (GMT-5)' },
  { value: 'America/Mazatlan', label: 'Pacífico — Mazatlán, Chihuahua (GMT-7)' },
  { value: 'America/Hermosillo', label: 'Sonora — Hermosillo, sin horario de verano (GMT-7)' },
  { value: 'America/Tijuana', label: 'Noroeste — Tijuana, Baja California (GMT-8)' },
]

export type RawNodeType =
  | 'msg'
  | 'ask'
  | 'act'
  | 'dec'
  | 'answer'
  | 'route'
  | 'call'
  | 'handoff'
  | 'fin'

export interface RawNode {
  id: string
  type: RawNodeType
  position?: { x: number; y: number }
  /** Optional editor-only grouping label (e.g. "Identificación", "Agendado").
   *  The runtime ignores it; it round-trips as node metadata so the operator
   *  can think of the flow as phases of steps. */
  phase?: string
  config: Record<string, unknown>
}

export interface RawEdge {
  from: string
  to: string
  when?: string
  label?: string
}

export interface RawFlow {
  nodes: RawNode[]
  edges: RawEdge[]
}

export interface WorkerFlow {
  id: string
  name: string
  status: string
  /** 'orchestrator' = the agent's main flow (runs from message 1). Present on
   *  the owner flows API; may be absent on the operator API for now. */
  role?: 'task' | 'orchestrator'
  version: number
  flow: RawFlow
  /** El compilador escribió esta versión (D11). Presente sólo en la API del
   *  dueño (`/me/bots/:botId/flows`); la del operador todavía no la manda, y
   *  `undefined` significa "no se sabe", no "a mano".
   *
   *  Desde W4'-4b es la procedencia con la que la pantalla decide autoría y solo
   *  lectura (`isConfigOwnedFlow`). En la consola del operador, donde no viaja,
   *  eso se lee como "no administrado": ningún letrero y ningún candado, que es
   *  lo mismo que esa consola hacía antes y lo único honesto cuando el dato no
   *  llegó. */
  managed?: boolean
  /** De qué proceso sale este flujo (F2-3), derivado del menú del flujo
   *  principal. `null` cuando la derivación no lo alcanza: nunca se pinta un
   *  proceso por descarte. Ausente en la API del operador. */
  process?: { key: string; name: string } | null
  /** Si el flujo sigue coincidiendo con la configuración del cliente (F4-3).
   *  `count` son los renglones de deriva que el servidor encontró. Ausente en la
   *  API del operador, que no la calcula. */
  drift?: { state: FlowDriftState; count: number }
}

/** El veredicto de frescura de un flujo, tal como lo manda `/me/bots/:b/flows`.
 *  `factory` es la captura con la que nace el agente: existe, funciona, y
 *  todavía no sale de nada que el cliente haya configurado. */
export type FlowDriftState = 'synced' | 'drift' | 'factory' | 'none'

/** Si el flujo SALE de la configuración del cliente (F4-3). Un solo predicado
 *  para los dos lugares que lo preguntan: la etiqueta de autoría del selector y
 *  la presentación de solo lectura del editor (F4-9).
 *
 *  SE LEE `managed`, Y NADA MÁS (W4'-4b). Es la PROCEDENCIA de verdad: la
 *  columna la escribe el compilador al publicar, así que una fila administrada
 *  es, literalmente, una fila que la configuración escribió y que la siguiente
 *  publicación va a volver a escribir. Antes esto se derivaba del veredicto de
 *  deriva (`synced` o `drift`), que era lo único que decía la verdad mientras
 *  nadie prendía la columna: una heurística honesta, con fecha de caducidad.
 *  Ya caducó, y una heurística que sobrevive a su motivo es una segunda verdad
 *  sobre el mismo hecho.
 *
 *  Lo que la heurística no sabía y esto sí:
 *   - El FLUJO PRINCIPAL. Antes se excluía por rol, porque el orquestador nunca
 *     salía de una ficha. Desde que el Publicar compila el agente entero, el
 *     principal administrado lo escribe el compilador igual que a los demás, y
 *     editarlo a mano se perdería en la siguiente publicación. Ya no hay que
 *     excluirlo: el que todavía no se publicó no está administrado y se sigue
 *     editando, y el publicado se presenta como lo que es.
 *   - La captura DE FÁBRICA (F4-10) sigue sin etiqueta, ahora por la razón
 *     directa: nace con el agente y nadie la compiló, así que no está
 *     administrada.
 *
 *  Lo demás no lleva etiqueta de autoría: decir "manual" de todo lo que no
 *  reconocemos sería un letrero pintado con otras palabras. */
export function isConfigOwnedFlow(f: WorkerFlow): boolean {
  return f.managed === true
}

/** La etiqueta de autoría, dicha con la MISMA regla que `isConfigOwnedFlow`: un
 *  letrero que dijera una cosa y una pantalla de solo lectura que dijera otra
 *  son dos verdades para un solo hecho, y una de las dos estaría mintiendo. */
function authorshipLabel(f: WorkerFlow): string | null {
  return isConfigOwnedFlow(f) ? 'de tu configuración' : null
}

/** La etiqueta del selector de flujos del encabezado. Corta a propósito: cabe
 *  en el header. " · principal" se conserva TAL CUAL (copia fijada). */
export function flowSelectLabel(f: WorkerFlow): string {
  const authorship = authorshipLabel(f)
  // El principal administrado TAMBIÉN lo escribe la configuración, y su
  // etiqueta lo dice con la misma regla que las demás: un banner de solo
  // lectura que lo anuncia y un selector que lo calla serían dos verdades
  // para un solo hecho.
  if (f.role === 'orchestrator') {
    return authorship ? `${f.name} · principal · ${authorship}` : `${f.name} · principal`
  }
  const head = authorship ? `${f.name} · ${authorship}` : f.name
  return f.process ? `${head}, Proceso: ${f.process.name}` : head
}

/** La línea de procedencia bajo el nombre del flujo en la tarjeta del agente:
 *  de qué proceso sale y quién lo escribió. */
export function flowProvenanceMeta(f: WorkerFlow): string {
  if (f.role === 'orchestrator') {
    // La misma regla que el selector: administrado = de tu configuración.
    const authorship = authorshipLabel(f)
    return authorship ? `Flujo principal · ${authorship}` : 'Flujo principal'
  }
  const parts: string[] = []
  if (f.process) parts.push(`Proceso: ${f.process.name}`)
  const authorship = authorshipLabel(f)
  if (authorship) parts.push(authorship)
  return parts.join(' · ')
}

/** The agent's general (non-flow) settings the one editor exposes alongside the
 *  conversation: the handoff "safety net" (when to give up and pass to a person).
 *  Real runtime config: the Skill runtime (`run-skill.ts`, answer node) escalates
 *  once `bot_failed_response_attempts` reaches
 *  `reglas.failed_response_attempts_before_handoff`, and the classic path's
 *  `handoff-check.ts` reads the same field. Edited via the owner worker endpoint. */
export interface AgentSettings {
  failedAttempts: number
  /** The guided-identity answers the agent's `identidad` is composed from. Null
   *  when the bot has none yet (legacy/template) — the form starts empty. This
   *  is how the owner sets WHO the agent is without writing a prompt. */
  identityProfile: IdentityProfile | null
  /** conocimiento.no_match_message — what Responder says when the knowledge base
   *  has no answer. Empty = the system default deferral. */
  noMatchMessage: string
  /** WhatsApp style: allow bold/italics emphasis (off = plain text). */
  useFormatting: boolean
  /** WhatsApp style: allow occasional emojis (off = none). */
  useEmojis: boolean
  /** Booking mode: true = a coordinator confirms each cita; false = autonomous. */
  requiresConfirmation: boolean
  /** horarios.business_hours as a Mon-first (0=Mon) week; gates the off-hours
   *  auto-reply. Owner-editable (was operator-only). */
  businessHours: { day: number; open: boolean; from?: string; to?: string }[]
  /** horarios.out_of_hours_message — sent outside business hours ('' = none). */
  outOfHoursMessage: string
  /** horarios.proactive_window.timezone — the tz the agent schedules citas in. */
  timezone: string
}

/** A blocking validator error returned by the save endpoint (per step). */
export interface FlowSaveError {
  code: string
  message: string
  nodeId?: string
}

export interface FlowClientError {
  nodeId?: string
  rule: string
  message: string
}

/** Client-side mirror of the engine's structural validation rules — the
 *  blocking ones that need no tool registry or bot config (validate-flow.ts
 *  rules 1–4). Runs on every edit so the editor's validity badge updates live;
 *  the save endpoint stays authoritative for the rest (tool args, forbidden
 *  phrases, undefined vars). */
export function validateFlowClient(flow: RawFlow): FlowClientError[] {
  const errors: FlowClientError[] = []
  if (flow.nodes.length === 0) {
    errors.push({ rule: 'empty_flow', message: 'El flujo no tiene pasos todavía.' })
    return errors
  }
  const start = flow.nodes.find((n) => n.id === 'start') ?? flow.nodes[0]!
  const fins = flow.nodes.filter((n) => n.type === 'fin')

  if (!fins.some((n) => n.config.kind === 'ok')) {
    errors.push({
      rule: 'missing_fin_ok',
      message: 'Falta un final que resuelva y regrese al agente (Fin · OK).',
    })
  }
  // Escalation is covered by the safety net + an optional "Pasar con asesor"
  // node, so a flow-level handoff terminal is no longer required (editor overhaul
  // L3). `validateFlowClient` only returns BLOCKING errors, so this check is
  // simply dropped here; the server returns it as a non-blocking warning.
  for (const f of fins) {
    if (f.config.kind === 'handoff' && !f.config.reason) {
      errors.push({
        nodeId: f.id,
        rule: 'fin_handoff_missing_reason',
        message: 'Este final "pasar a una persona" necesita un motivo.',
      })
    }
  }

  // Reachability — BFS from the start node.
  const reachable = new Set<string>()
  const queue = [start.id]
  while (queue.length > 0) {
    const id = queue.shift()!
    if (reachable.has(id)) continue
    reachable.add(id)
    for (const e of flow.edges) if (e.from === id) queue.push(e.to)
  }
  for (const n of flow.nodes) {
    if (!reachable.has(n.id)) {
      errors.push({
        nodeId: n.id,
        rule: 'orphan_node',
        message: 'Este paso no se puede alcanzar desde el inicio del flujo.',
      })
    }
  }

  // Dead ends — a non-fin node with no outgoing edge stalls the flow.
  for (const n of flow.nodes) {
    if (n.type !== 'fin' && !flow.edges.some((e) => e.from === n.id)) {
      errors.push({
        nodeId: n.id,
        rule: 'dead_end_node',
        message: 'Este paso no continúa a ningún lado; elige el siguiente paso.',
      })
    }
  }

  // Call nodes must point at a sub-flow to run (mirrors the engine's
  // `call_no_target` rule). Live feedback so the editor flags an unassigned
  // "Llamar subflujo" box before save.
  for (const n of flow.nodes) {
    if (n.type === 'call' && !(typeof n.config.skill_id === 'string' && n.config.skill_id.trim())) {
      errors.push({
        nodeId: n.id,
        rule: 'call_no_target',
        message: 'Este paso no tiene un subflujo asignado; elige cuál ejecutar.',
      })
    }
  }

  return errors
}

const enc = encodeURIComponent

/** A registered act-node tool (orchestrator M5a) — for the editor's tool picker
 *  so capabilities like `capturar_lead` (Guardar en CRM) are discoverable. */
export interface ToolInfo {
  name: string
  description: string
}

/** GET /tools — the registered act-node tools, for the editor's tool picker. */
export async function getTools(): Promise<ToolInfo[]> {
  const res = await apiClient.get<{ data: ToolInfo[] }>('/tools')
  return res.data.data
}

/**
 * GET /admin/tenants/:t/bots/:b/skills — a worker's raw Skill flows with full
 * node config (the faithful round-trip source, unlike the lossy console view).
 */
export async function listFlows(tenantId: string, botId: string): Promise<WorkerFlow[]> {
  const res = await apiClient.get<{ data: WorkerFlow[] }>(
    `/admin/tenants/${enc(tenantId)}/bots/${enc(botId)}/skills`,
  )
  return res.data.data
}

export interface SkillTemplate {
  id: string
  name: string
  description: string
}

/** The starter-flow catalog + the bot's vertical (so the wizard can offer that
 *  vertical's intent vocabulary as the flow's trigger). */
export interface TemplateCatalog {
  vertical: string
  templates: SkillTemplate[]
}

/** The starter-flow catalog for the client's vertical. */
export async function listSkillTemplates(
  tenantId: string,
  botId: string,
): Promise<TemplateCatalog> {
  const res = await apiClient.get<{ data: SkillTemplate[]; vertical: string }>(
    `/admin/tenants/${enc(tenantId)}/bots/${enc(botId)}/skill-templates`,
  )
  return { vertical: res.data.vertical, templates: res.data.data }
}

/** One stage of one of the tenant's funnels, for the `capturar_lead` pickers.
 *  The list spans EVERY pipeline the tenant owns (PR 17), so each option has to
 *  carry which one it belongs to: a stage key is only unique within a pipeline,
 *  and the editor filters the stage options by the chosen funnel. */
export interface FunnelStageOption {
  key: string
  name: string
  kind: 'open' | 'won' | 'lost'
  /** The funnel this stage belongs to. */
  pipelineKey: string
  pipelineName: string
  /** Where a capture with no explicit `embudo` lands. */
  isDefaultPipeline: boolean
  /** Lo que la etapa dice que obtiene, ya con sus etiquetas (CP3 L). El editor
   *  lo muestra como pista; no valida nada con ello. Puede llegar vacío. */
  gatheredLabels?: string[]
  /** Las mismas cosas que `gatheredLabels`, en el mismo orden, pero con la CLAVE
   *  cruda (C2). El picker de "Recordar" escribe la clave; la etiqueta solo se
   *  lee. Opcional por la misma razón que su gemela: un servidor anterior no la
   *  manda, y el editor no debe romperse por eso. */
  gatheredKeys?: string[]
}

/** Un dato de la ficha de un proceso, tal como el editor lo ofrece: la clave que
 *  se escribe, el nombre que el espacio le puso, y — si es una lista — sus
 *  opciones, que es lo que convierte una Pregunta en `enum` en vez de texto
 *  libre (C2). Misma forma que `PipelineFieldView` en la pantalla de Procesos:
 *  es el mismo dato visto desde otra pantalla. */
export interface FichaFieldOption {
  key: string
  label: string
  dataType: string
  /** Ya resueltas contra su fuente cuando el dato tiene una (D21-1). */
  options: string[] | null
  /** DE DÓNDE SALEN (D21-4). El servidor la manda desde D21-1
   *  (`pipelineFichas`), y hasta ahora el editor la tiraba: una Pregunta
   *  escrita a mano sobre un dato con fuente se horneaba con la lista del día
   *  y ahí se quedaba, mientras que la MISMA pregunta compilada desde la ficha
   *  llevaba su `source` y volvía a resolverla al contestar. Dos caminos, dos
   *  conductas, un solo dato.
   *
   *  Opcional por la misma razón que `gatheredKeys`: un servidor anterior no la
   *  manda, y ausente significa `lista`, que es la conducta de siempre. */
  optionsSource?: string
}

/** Lo que ambas rutas de pipeline-stages contestan ahora: las etapas Y la ficha
 *  de cada proceso (por clave de proceso). Viajan juntas a propósito — son las
 *  dos mitades de una sola pregunta ("¿a dónde aterriza esta captura y qué datos
 *  lleva ese proceso?"), y pedirlas por separado es cómo las dos listas terminan
 *  contestadas en momentos distintos y sin coincidir. */
export interface PipelineStagesPayload {
  stages: FunnelStageOption[]
  ficha: Record<string, FichaFieldOption[]>
}

/** GET /admin/tenants/:t/bots/:b/pipeline-stages — the CLIENT's funnel stages
 *  plus each process's ficha. Tolerant defaults on purpose: an older server that
 *  answers without `ficha` must degrade to "no datos to offer", never crash the
 *  editor. */
export async function listPipelineStages(
  tenantId: string,
  botId: string,
): Promise<PipelineStagesPayload> {
  const res = await apiClient.get<{
    data?: FunnelStageOption[]
    ficha?: Record<string, FichaFieldOption[]>
  }>(`/admin/tenants/${enc(tenantId)}/bots/${enc(botId)}/pipeline-stages`)
  return { stages: res.data.data ?? [], ficha: res.data.ficha ?? {} }
}

/** GET /me/bots/:b/pipeline-stages — the owner twin, same payload. */
export async function ownerListPipelineStages(botId: string): Promise<PipelineStagesPayload> {
  const res = await apiClient.get<{
    data?: FunnelStageOption[]
    ficha?: Record<string, FichaFieldOption[]>
  }>(`/me/bots/${enc(botId)}/pipeline-stages`)
  return { stages: res.data.data ?? [], ficha: res.data.ficha ?? {} }
}

/**
 * POST /admin/tenants/:t/bots/:b/skills — creates a flow, blank or from a
 * `templateId` out of the vertical's starter catalog.
 */
export async function createFlow(
  tenantId: string,
  botId: string,
  input: { name: string; intent?: string; templateId?: string; role?: 'task' | 'orchestrator' },
): Promise<{ id: string; name: string }> {
  const res = await apiClient.post<{ data: { id: string; name: string } }>(
    `/admin/tenants/${enc(tenantId)}/bots/${enc(botId)}/skills`,
    input,
  )
  return res.data.data
}

/**
 * PUT /admin/tenants/:t/bots/:b/skills/:skillId/flow — persists the raw flow.
 * The engine validator is authoritative; a 400 carries per-step errors
 * (unpack with flowSaveErrors).
 */
export async function saveFlow(
  tenantId: string,
  botId: string,
  skillId: string,
  flow: RawFlow,
  expectedVersion: number,
): Promise<void> {
  await apiClient.put(
    `/admin/tenants/${enc(tenantId)}/bots/${enc(botId)}/skills/${enc(skillId)}/flow`,
    { flow, expected_version: expectedVersion },
  )
}

/** Operator only: promote a flow to a reusable starter template for the
 *  client's vertical (shows up in everyone's "Nuevo flujo" picker). */
export async function saveFlowAsTemplate(
  tenantId: string,
  botId: string,
  skillId: string,
  input: { name: string; description: string },
): Promise<void> {
  await apiClient.post(
    `/admin/tenants/${enc(tenantId)}/bots/${enc(botId)}/skills/${enc(skillId)}/save-as-template`,
    input,
  )
}

// ─── Owner-facing variants (tenant-scoped /me/bots/:botId/flows) ─────────────
// The same box editor, but for a tenant owner editing their OWN agent — no
// tenantId in the URL (it comes from the session), gated server-side on the
// tenant.bots.skills permission.

/** GET /me/bots/:botId/flows — the owner's own agent flows (tenant from the session). */
export async function ownerListFlows(botId: string): Promise<WorkerFlow[]> {
  const res = await apiClient.get<{ data: WorkerFlow[] }>(`/me/bots/${enc(botId)}/flows`)
  return res.data.data
}

/** GET /me/bots/:botId/flow-templates — the starter catalog for the tenant's vertical. */
export async function ownerListTemplates(botId: string): Promise<TemplateCatalog> {
  const res = await apiClient.get<{ data: SkillTemplate[]; vertical: string }>(
    `/me/bots/${enc(botId)}/flow-templates`,
  )
  return { vertical: res.data.vertical, templates: res.data.data }
}

/**
 * POST /me/bots/:botId/flows — owner-side flow creation; the only surface that
 * accepts `role: 'orchestrator'` (the agent's main flow).
 */
export async function ownerCreateFlow(
  botId: string,
  input: { name: string; intent?: string; templateId?: string; role?: 'task' | 'orchestrator' },
): Promise<{ id: string; name: string }> {
  const res = await apiClient.post<{ data: { id: string; name: string } }>(
    `/me/bots/${enc(botId)}/flows`,
    input,
  )
  return res.data.data
}

/** PUT /me/bots/:botId/flows/:skillId — owner-side save; 400 details unpack via
 *  flowSaveErrors, and a 409 `flow_version_conflict` means somebody (a person or
 *  the capture-flow generator) published a newer version while this one was open. */
export async function ownerSaveFlow(
  botId: string,
  skillId: string,
  flow: RawFlow,
  expectedVersion: number,
): Promise<void> {
  await apiClient.put(`/me/bots/${enc(botId)}/flows/${enc(skillId)}`, {
    flow,
    expected_version: expectedVersion,
  })
}

/** DELETE /me/bots/:botId/flows/:skillId — removes the whole flow, every version. */
export async function ownerDeleteFlow(botId: string, skillId: string): Promise<void> {
  await apiClient.delete(`/me/bots/${enc(botId)}/flows/${enc(skillId)}`)
}

// ─── Flow gateway — binds the editor to either the operator or owner API ──────

export interface FlowGateway {
  /** React-Query cache scope (distinguishes operator vs owner + which bot). */
  scopeKey: string
  /** Where the editor's back button + breadcrumb point. */
  backHref: string
  breadcrumb: { label: string; href: string }[]
  list: () => Promise<WorkerFlow[]>
  templates: () => Promise<TemplateCatalog>
  /** The funnel stages a `capturar_lead` node may target, across every pipeline
   *  the tenant owns, PLUS each process's ficha (the datos the pickers offer).
   *  Tenant data — the editor must never offer a funnel, a stage or a dato of
   *  its own. */
  pipelineStages: () => Promise<PipelineStagesPayload>
  create: (input: {
    name: string
    intent?: string
    templateId?: string
    role?: 'task' | 'orchestrator'
  }) => Promise<{
    id: string
    name: string
  }>
  /** `expectedVersion` is the version the editor LOADED. The server refuses the
   *  save with 409 when it is no longer the live one — see FLOW_VERSION_CONFLICT. */
  save: (skillId: string, flow: RawFlow, expectedVersion: number) => Promise<void>
  /** Present only for operators (INHERTA staff): promote a flow to a reusable
   *  vertical template. Omitted for owners, so the editor hides the action. */
  saveAsTemplate?: (skillId: string, input: { name: string; description: string }) => Promise<void>
  /** Whether this surface can create the agent's orchestrator (main) flow.
   *  Owner-only for now: the owner create route accepts `role`. */
  supportsOrchestrator?: boolean
  /** Delete a whole flow (all versions). Owner-only for now. */
  deleteFlow?: (skillId: string) => Promise<void>
  /** The agent's general (non-flow) settings — the handoff safety net. Present
   *  only on the owner gateway (the `/workers` endpoint is tenant-side); the one
   *  editor shows an "Ajustes" panel when this exists. */
  settings?: {
    load: () => Promise<AgentSettings>
    save: (patch: AgentSettings) => Promise<void>
  }
  /** The Asesores a `handoff` node can route to (owner-only; the operator's team
   *  isn't the edited tenant's). When present, the handoff node shows an
   *  "assign to" picker; absent → handoffs go to the pool. */
  assignableMembers?: () => Promise<AssignableMember[]>
  /** Manage the agent's knowledge base (owner-only). When present, the editor
   *  shows a "Conocimiento" panel so the client uploads/approves their own KB
   *  without the operator console. */
  kb?: KbApi
  /** Page destinations (C2, owner side): when set, the editor's Ajustes /
   *  Conocimiento buttons LINK there instead of opening modals — the same
   *  content is a real URL that Reports, onboarding and support can name. */
  settingsHref?: string
  kbHref?: string
  /** Chat with the agent inside the editor (owner-only). When present, the editor
   *  shows a "Probar" panel that runs the real bot with its outbound sinked, so
   *  the client tests a flow + KB without messaging the WhatsApp number. */
  simulator?: SimulatorApi
  /** Where the tenant's funnel board lives, for one process (F6-1). Present
   *  only on the owner gateway: /prospectos is a tenant route and staff hold no
   *  membership in the client's espacio, so the operator twin shows the same
   *  line as plain text instead of a door it cannot open. */
  boardHref?: (pipelineId: string) => string
}

/** Operator (INHERTA staff) editing a client's agent — tenantId is in the URL. */
export function operatorFlowGateway(tenantId: string, botId: string): FlowGateway {
  return {
    scopeKey: `op:${tenantId}:${botId}`,
    backHref: `/admin/tenants/${tenantId}/bots/${botId}`,
    breadcrumb: [
      { label: 'Clientes', href: '/admin/tenants' },
      { label: 'Cliente', href: `/admin/tenants/${tenantId}` },
    ],
    list: () => listFlows(tenantId, botId),
    templates: () => listSkillTemplates(tenantId, botId),
    pipelineStages: () => listPipelineStages(tenantId, botId),
    create: (input) => createFlow(tenantId, botId, input),
    save: (skillId, flow, expectedVersion) =>
      saveFlow(tenantId, botId, skillId, flow, expectedVersion),
    saveAsTemplate: (skillId, input) => saveFlowAsTemplate(tenantId, botId, skillId, input),
    // B1: the editor renders "Probar" purely off this capability — the same
    // panel the owner gets, now against the /admin twin routes.
    simulator: operatorSimulatorApi(tenantId, botId),
    // B3: Ajustes/Perfil for staff — the same projection the owner edits,
    // through the /admin twins (shared core server-side).
    settings: {
      load: async () => {
        const e = await getAdminWorkerEditable(tenantId, botId)
        return {
          failedAttempts: e.handoff.failedAttempts,
          identityProfile: e.identityProfile,
          noMatchMessage: e.noMatchMessage,
          useFormatting: e.useFormatting,
          useEmojis: e.useEmojis,
          requiresConfirmation: e.requiresConfirmation,
          businessHours: e.businessHours,
          outOfHoursMessage: e.outOfHoursMessage,
          timezone: e.timezone,
        }
      },
      save: (patch) =>
        saveAdminWorkerEditable(tenantId, botId, {
          handoff: { failedAttempts: patch.failedAttempts },
          ...(patch.identityProfile ? { identityProfile: patch.identityProfile } : {}),
          noMatchMessage: patch.noMatchMessage,
          useFormatting: patch.useFormatting,
          useEmojis: patch.useEmojis,
          requiresConfirmation: patch.requiresConfirmation,
          businessHours: patch.businessHours,
          outOfHoursMessage: patch.outOfHoursMessage,
          timezone: patch.timezone,
        }),
    },
    // B3: the CLIENT's team (not the operator's own) — so a handoff node can
    // name an Asesor. Same shape the owner gateway feeds the picker.
    assignableMembers: async () =>
      (await getTenantMembers(tenantId))
        .filter((m) => m.status === 'active')
        .map((m) => ({ id: m.id, name: m.name, role: m.role })),
  }
}

/** A tenant owner editing their OWN agent — tenant comes from the session. */
export function ownerFlowGateway(botId: string): FlowGateway {
  return {
    scopeKey: `me:${botId}`,
    backHref: '/asistente',
    breadcrumb: [{ label: 'Agentes', href: '/asistente' }],
    list: () => ownerListFlows(botId),
    templates: () => ownerListTemplates(botId),
    pipelineStages: () => ownerListPipelineStages(botId),
    create: (input) => ownerCreateFlow(botId, input),
    save: (skillId, flow, expectedVersion) => ownerSaveFlow(botId, skillId, flow, expectedVersion),
    supportsOrchestrator: true,
    deleteFlow: (skillId) => ownerDeleteFlow(botId, skillId),
    settings: {
      load: async () => {
        const e = await getWorkerEditable(botId)
        return {
          failedAttempts: e.handoff.failedAttempts,
          identityProfile: e.identityProfile,
          noMatchMessage: e.noMatchMessage,
          useFormatting: e.useFormatting,
          useEmojis: e.useEmojis,
          requiresConfirmation: e.requiresConfirmation,
          businessHours: e.businessHours,
          outOfHoursMessage: e.outOfHoursMessage,
          timezone: e.timezone,
        }
      },
      save: (patch) =>
        saveWorkerEditable(botId, {
          handoff: { failedAttempts: patch.failedAttempts },
          ...(patch.identityProfile ? { identityProfile: patch.identityProfile } : {}),
          noMatchMessage: patch.noMatchMessage,
          useFormatting: patch.useFormatting,
          useEmojis: patch.useEmojis,
          requiresConfirmation: patch.requiresConfirmation,
          businessHours: patch.businessHours,
          outOfHoursMessage: patch.outOfHoursMessage,
          timezone: patch.timezone,
        }),
    },
    assignableMembers: getAssignableMembers,
    kb: ownerKbApi(botId),
    settingsHref: `/asistente/${botId}/ajustes`,
    kbHref: `/asistente/${botId}/conocimiento`,
    simulator: ownerSimulatorApi(botId),
    // `proceso` is the param the board's funnel selector reads and writes
    // (packages/web/src/pages/leads/Leads.tsx), so the link lands on the board
    // already showing the process the card went to.
    boardHref: (pipelineId) => `/prospectos?proceso=${encodeURIComponent(pipelineId)}`,
  }
}

/** Pull the validator's per-step errors out of a 400 save response. */
export function flowSaveErrors(err: unknown): FlowSaveError[] {
  if (isAxiosError(err)) {
    const details = (err.response?.data as { error?: { details?: FlowSaveError[] } } | undefined)
      ?.error?.details
    if (Array.isArray(details)) return details
  }
  return []
}
